import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { resetPassword } from "../../redux/slices/auth";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import InputPassword from "../../components/InputPassword/InputPassword";
import { toast } from "react-toastify";
import "./ResetPasswordForm.css";

const ResetPasswordForm = () => {
  const dispatch = useDispatch();
  const { status } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const key = searchParams.get('key');
  const login = searchParams.get('login');

  useEffect(() => {
    if (!key || !login) {
      navigate('/forgot-password');
    }
  }, [key, login, navigate]);

  const validationSchema = Yup.object().shape({
    new_password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .matches(/(?=.*[a-z])/, "Password must contain at least one lowercase letter")
      .matches(/(?=.*[A-Z])/, "Password must contain at least one uppercase letter")
      .matches(/(?=.*\d)/, "Password must contain at least one number")
      .matches(/(?=.*[@$!%*?&])/, "Password must contain at least one special character")
      .required("New password is required"),
    confirm_password: Yup.string()
      .oneOf([Yup.ref('new_password'), null], 'Passwords must match')
      .required("Please confirm your password"),
  });

  const formik = useFormik({
    initialValues: {
      login: "",
      new_password: "",
      confirm_password: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const result = await dispatch(resetPassword({
          key: key,
          login: login,
          new_password: values.new_password
        }));
        
        if (result.payload.success) {
          toast.success(result.payload.message);
          navigate('/login');
        } else {
          toast.error(result.payload.message);
        }
      } catch (err) {
        toast.error("Failed to reset password. Please try again.");
      }
    },
  });

  if (!key || !login) {
    return null;
  }

  return (
    <section className="ResetPasswordForm">
      <div className="reset-password-form-wrapper">
        <form onSubmit={formik.handleSubmit}>
          <h3>Set New Password</h3>
          <p>Enter your new password below</p>

          {/* New Password Field */}
          <div className="reset-password-form-group">
            <InputPassword
              name="new_password"
              placeholder="New Password"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.new_password}
              className={
                formik.touched.new_password && formik.errors.new_password ? "error" : ""
              }
            />
            {formik.touched.new_password && formik.errors.new_password && (
              <div className="error-message">
                <p>{formik.errors.new_password}</p>
              </div>
            )}
          </div>

          {/* Confirm Password Field */}
          <div className="reset-password-form-group">
            <InputPassword
              name="confirm_password"
              placeholder="Confirm New Password"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.confirm_password}
              className={
                formik.touched.confirm_password && formik.errors.confirm_password ? "error" : ""
              }
            />
            {formik.touched.confirm_password && formik.errors.confirm_password && (
              <div className="error-message">
                <p>{formik.errors.confirm_password}</p>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status === "loading" || formik.isSubmitting}
          >
            {status === "loading" ? "Resetting..." : "Reset Password"}
          </button>

          {/* Back to Login Link */}
          <p className="back-to-login">
            Remember your password? <Link to="/login">Back to Login</Link>
          </p>
        </form>
      </div>
    </section>
  );
};

export default ResetPasswordForm;